import type { LensCalendar } from '~/types/leLens';

export const LENS_DEFAULT_CALENDAR: LensCalendar = {
  zone: 'Australia/Sydney',
  weekStart: 'monday',
  fiscalYearStart: 7,
};

export const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export type WallClock = { year: number; month: number; day: number; hour: number; minute: number; second: number };

const formatters = new Map<string, Intl.DateTimeFormat>();

function formatterFor(zone: string): Intl.DateTimeFormat {
  let formatter = formatters.get(zone);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: zone,
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
    formatters.set(zone, formatter);
  }
  return formatter;
}

export function wallClockOf(instant: Date, zone = LENS_DEFAULT_CALENDAR.zone): WallClock {
  const parts = formatterFor(zone).formatToParts(instant);
  const read = (type: Intl.DateTimeFormatPartTypes) => Number(parts.find((part) => part.type === type)?.value);
  return {
    year: read('year'),
    month: read('month'),
    day: read('day'),
    hour: read('hour') % 24,
    minute: read('minute'),
    second: read('second'),
  };
}

function asUtc(wall: WallClock): number {
  return Date.UTC(wall.year, wall.month - 1, wall.day, wall.hour, wall.minute, wall.second);
}

function offsetMs(instant: Date, zone: string): number {
  return asUtc(wallClockOf(instant, zone)) - Math.floor(instant.getTime() / 1000) * 1000;
}

export function instantAt(wall: WallClock, zone = LENS_DEFAULT_CALENDAR.zone): Date {
  const local = asUtc(wall);
  const first = new Date(local - offsetMs(new Date(local), zone));
  const second = new Date(local - offsetMs(first, zone));
  const candidates = [first, second].filter((candidate) => asUtc(wallClockOf(candidate, zone)) === local);
  if (!candidates.length) {
    return new Date(Math.max(first.getTime(), second.getTime()));
  }
  return new Date(Math.min(...candidates.map((candidate) => candidate.getTime())));
}

/** The civil day an instant falls on in a zone, as YYYY-MM-DD. */
export function civilDayOf(instant: Date, zone = LENS_DEFAULT_CALENDAR.zone): string {
  const wall = wallClockOf(instant, zone);
  return `${wall.year}-${pad(wall.month)}-${pad(wall.day)}`;
}

/** The last completed civil day: yesterday in the zone. */
export function completedDayOf(now = new Date(), zone = LENS_DEFAULT_CALENDAR.zone): string {
  return shiftDay(civilDayOf(now, zone), -1);
}

export function shiftDay(day: string, days: number): string {
  const shifted = new Date(`${day}T00:00:00Z`);
  shifted.setUTCDate(shifted.getUTCDate() + days);
  return shifted.toISOString().slice(0, 10);
}

/** The first day of the week holding a day, under the calendar's week start. */
export function weekStartOf(
  day: string,
  weekStart: LensCalendar['weekStart'] = LENS_DEFAULT_CALENDAR.weekStart,
): string {
  const weekday = new Date(`${day}T00:00:00Z`).getUTCDay();
  const offset = weekStart === 'monday' ? (weekday + 6) % 7 : weekday;
  return shiftDay(day, -offset);
}

export function isCivilDay(value: unknown): value is string {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return false;
  }
  const [year, month, day] = value.split('-').map(Number);
  const parsed = new Date(Date.UTC(year, month - 1, day));
  return parsed.getUTCFullYear() === year && parsed.getUTCMonth() === month - 1 && parsed.getUTCDate() === day;
}

function partsOfDay(day: string): { year: number; month: number; day: number } {
  const [year, month, date] = day.split('-').map(Number);
  return { year, month, day: date };
}

export type YearInView = number | 'always';

/** `24 Sep`, with the year only when it is not the year in view. */
export function dayLabel(day: string, yearInView: YearInView = new Date().getUTCFullYear()): string {
  if (!isCivilDay(day)) {
    return day;
  }
  const parts = partsOfDay(day);
  return `${parts.day} ${MONTHS[parts.month - 1]}${yearSuffix(parts.year, yearInView)}`;
}

/** `18–24 Sep`, `26 Aug – 24 Sep`, `24 Sep 2025 – 3 Jan 2026`: inclusive civil days as the server writes them. */
export function spanLabel(from: string, to: string, yearInView: YearInView = new Date().getUTCFullYear()): string {
  if (!isCivilDay(from) || !isCivilDay(to)) {
    return `${from} – ${to}`;
  }
  const start = partsOfDay(from);
  const end = partsOfDay(to);
  if (from === to) {
    return dayLabel(to, yearInView);
  }
  if (start.year === end.year && start.month === end.month) {
    return `${start.day}–${end.day} ${MONTHS[end.month - 1]}${yearSuffix(end.year, yearInView)}`;
  }
  if (start.year === end.year) {
    return `${start.day} ${MONTHS[start.month - 1]} – ${end.day} ${MONTHS[end.month - 1]}${yearSuffix(end.year, yearInView)}`;
  }
  return `${start.day} ${MONTHS[start.month - 1]} ${start.year} – ${end.day} ${MONTHS[end.month - 1]} ${end.year}`;
}

/** `2:37 pm`, `2 pm`, `12 am`. */
export function timeLabel(wall: Pick<WallClock, 'hour' | 'minute'>): string {
  const twelve = wall.hour % 12 === 0 ? 12 : wall.hour % 12;
  const meridiem = wall.hour < 12 ? 'am' : 'pm';
  return wall.minute ? `${twelve}:${String(wall.minute).padStart(2, '0')} ${meridiem}` : `${twelve} ${meridiem}`;
}

export function isMidnight(wall: WallClock): boolean {
  return wall.hour === 0 && wall.minute === 0 && wall.second === 0;
}

/** An instant as a reader writes it: the day alone at midnight, else the day and time. */
export function instantLabel(
  instant: Date,
  calendar: LensCalendar = LENS_DEFAULT_CALENDAR,
  yearInView?: number,
  alwaysShowTime = false,
): string {
  const wall = wallClockOf(instant, calendar.zone);
  const day = dayLabel(civilDayOf(instant, calendar.zone), yearInView ?? wall.year);
  return !alwaysShowTime && isMidnight(wall) ? day : `${day} ${timeLabel(wall)}`;
}

/** The wall clock in the calendar zone as ISO text with no offset, for pattern renderers. */
export function wallClockText(instant: Date, zone = LENS_DEFAULT_CALENDAR.zone): string {
  const wall = wallClockOf(instant, zone);
  return `${wall.year}-${pad(wall.month)}-${pad(wall.day)}T${pad(wall.hour)}:${pad(wall.minute)}:${pad(wall.second)}`;
}

const yearSuffix = (year: number, yearInView: YearInView) => (year === yearInView ? '' : ` ${year}`);

function pad(value: number): string {
  return String(value).padStart(2, '0');
}
